import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link } from "react-router";
import backgroundWebp from "../../assets/GradientBackground.webp";
import ShuttlerockLogo from "../../assets/ShuttlerockLogo.svg";
import { GlitchText } from "./GlitchText";

export interface ErrorPageProps {
  title: string;
  body: string;
  logoRoute?: string;
  primaryButton?: {
    onClick?: () => void;
    text: string;
    route?: string;
  };
  secondaryButton?: {
    text: string;
    route: string;
  };
}

export const ErrorPage = ({
  body,
  logoRoute = "/",
  title,
  primaryButton,
  secondaryButton,
}: ErrorPageProps) => {
  const getRouteProps = (route: string) => {
    return route.startsWith("http")
      ? { component: "a" as const, href: route }
      : { component: Link, to: route };
  };

  return (
    <Stack
      // Force LightTheme as this page is used universally but uses light theme's colors.
      data-light="true"
      sx={{
        backgroundImage: `url(${backgroundWebp})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundColor: "#3346AB",
        height: "100%",
      }}
    >
      <Stack
        sx={{ width: "100%", backgroundColor: ["grey.A50", "transparent"] }}
        alignItems={["center", "flex-start"]}
        pl={[null, 5]}
        py={[2, 3]}
      >
        <Box
          sx={{
            color: "text.primary",
            width: "240px",
            path: {
              fill: [null, "#fff"],
            },
          }}
          component={Link}
          to={logoRoute}
        >
          <ShuttlerockLogo data-testid="ShuttlerockLogo" style={{ display: "block" }} />
        </Box>
      </Stack>
      <Stack sx={{ flex: 1 }} justifyContent="center" alignItems="center">
        <Box sx={{ maxWidth: "650px" }} px={3}>
          <GlitchText
            variant="h1"
            color="primary.contrastText"
            mb={3}
            sx={{
              fontSize: ["3rem", "5rem"],
              lineHeight: 1,
              fontWeight: 700,
            }}
          >
            {title}
          </GlitchText>
          <Typography variant="subtitle1" component="p" color="primary.contrastText" mb={9}>
            {body}
          </Typography>
          <Stack direction={["column", "row"]} sx={{ gap: 2 }}>
            {primaryButton ? (
              <Button
                variant="contained"
                onClick={primaryButton?.onClick}
                {...(primaryButton?.route ? getRouteProps(primaryButton.route) : {})}
              >
                {primaryButton?.text}
              </Button>
            ) : null}
            {secondaryButton ? (
              <Button variant="contained" color="white" {...getRouteProps(secondaryButton?.route)}>
                {secondaryButton?.text}
              </Button>
            ) : null}
          </Stack>
        </Box>
      </Stack>
    </Stack>
  );
};
